import { useEffect, useRef } from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { ACCOUNT_ROUTES, findAccountRoute } from './accountRoutes'
import { cn } from '../../lib/utils'

export default function AccountLayout() {
  const location = useLocation()
  const active = findAccountRoute(location.pathname)
  const activeMobileLink = useRef<HTMLAnchorElement | null>(null)
  const content = useRef<HTMLDivElement | null>(null)
  const previousPath = useRef(location.pathname)

  useEffect(() => {
    activeMobileLink.current?.scrollIntoView?.({ block: 'nearest', inline: 'center' })
  }, [location.pathname])

  useEffect(() => {
    if (previousPath.current === location.pathname) return
    previousPath.current = location.pathname
    if (location.hash) return
    let observer: MutationObserver | null = null
    const focusHeading = () => {
      const heading = content.current?.querySelector<HTMLElement>('[data-account-heading]')
      if (!heading) return false
      heading.focus({ preventScroll: true })
      return true
    }
    const timer = window.setTimeout(() => {
      if (focusHeading() || !content.current) return
      observer = new MutationObserver(() => {
        if (focusHeading()) observer?.disconnect()
      })
      observer.observe(content.current, { childList: true, subtree: true })
    }, 0)
    return () => {
      window.clearTimeout(timer)
      observer?.disconnect()
    }
  }, [location.hash, location.pathname])

  return (
    <div className="mx-auto w-full max-w-6xl pb-[env(safe-area-inset-bottom)]">
      <div className="mb-5 lg:hidden">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Account</p>
        <nav aria-label="Account settings" className="-mx-1 overflow-x-auto px-1 pb-1">
          <div className="flex min-w-max gap-1">
            {ACCOUNT_ROUTES.map((route) => {
              const Icon = route.icon
              return (
                <NavLink
                  key={route.path}
                  to={route.path}
                  ref={route.path === active?.path ? activeMobileLink : undefined}
                  className={({ isActive }) => cn(
                    'inline-flex min-h-11 items-center gap-2 rounded-lg px-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                    isActive ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-secondary hover:text-foreground',
                  )}
                >
                  <Icon className="h-4 w-4" aria-hidden="true" />
                  {route.shortLabel}
                </NavLink>
              )
            })}
          </div>
        </nav>
      </div>

      <div className="grid min-w-0 gap-8 lg:grid-cols-[224px_minmax(0,1fr)]">
        <aside className="hidden lg:block">
          <div className="sticky top-6">
            <p className="mb-3 px-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Account</p>
            <nav aria-label="Account settings" className="space-y-1">
              {ACCOUNT_ROUTES.map((route) => {
                const Icon = route.icon
                return (
                  <NavLink
                    key={route.path}
                    to={route.path}
                    className={({ isActive }) => cn(
                      'flex min-h-11 items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                      isActive ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-secondary hover:text-foreground',
                    )}
                  >
                    <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                    {route.label}
                  </NavLink>
                )
              })}
            </nav>
          </div>
        </aside>

        <div ref={content} className="min-w-0">
          <Outlet />
        </div>
      </div>
    </div>
  )
}
